import { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import type { CheckoutAddress } from '../../types/checkout';

const AddressForm = lazy(() =>
  import('../address/AddressForm').then(m => ({ default: m.AddressForm }))
);

interface AddressSectionProps {
  addresses: CheckoutAddress[];
  selectedAddressId: number | null;
  onSelectAddress: (addressId: number) => void;
  onFormSuccess: (newAddressId?: number) => void;
  onContinue: () => void;
  isLoading: boolean;
  hideMap?: boolean;
  routes: { resolve: string; create: string };
  discountNeighborhood?: string | null;
  isAddressCompatible?: (address: CheckoutAddress) => boolean;
  discountWarning?: string | null;
  hideAddButton?: boolean;
  orgManageUrl?: string;
}

export function AddressSection({
  addresses,
  selectedAddressId,
  onSelectAddress,
  onFormSuccess,
  onContinue,
  isLoading,
  hideMap = false,
  routes,
  discountNeighborhood,
  isAddressCompatible,
  discountWarning,
  hideAddButton = false,
  orgManageUrl,
}: AddressSectionProps) {
  const [showForm, setShowForm] = useState(addresses.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (addresses.length > 0) {
      setShowForm(false);
    }
  }, [addresses.length]);

  const handleAddressSelect = useCallback((addressId: number) => {
    setError(null);
    onSelectAddress(addressId);
  }, [onSelectAddress]);

  const handleFormSuccess = useCallback(async (newAddressId?: number) => {
    setIsRefreshing(true);
    try {
      await onFormSuccess(newAddressId);
    } finally {
      setIsRefreshing(false);
      setShowForm(false);
    }
  }, [onFormSuccess]);

  const handleContinue = useCallback(() => {
    if (selectedAddressId === null) {
      setError('Selecciona una direccion');
      return;
    }
    const selectedAddress = addresses.find(a => a.id === selectedAddressId);
    if (selectedAddress && !selectedAddress.is_deliverable) {
      setError('Esta direccion esta fuera de nuestra zona de entrega');
      return;
    }
    setError(null);
    onContinue();
  }, [selectedAddressId, addresses, onContinue]);

  if (showForm) {
    return (
      <div>
        {isRefreshing ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center text-gray-500">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-gray-200 border-t-green-500" />
            <p className="mt-4 text-sm">Guardando direccion...</p>
          </div>
        ) : (
          <>
            <div className="mb-5">
              {addresses.length > 0 && (
                <button
                  type="button"
                  className="mb-4 flex items-center gap-2 border-none bg-transparent text-sm text-green-500 hover:text-green-600"
                  onClick={() => setShowForm(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Volver a mis direcciones
                </button>
              )}
              <h3 className="m-0 text-lg font-semibold text-gray-800">Nueva direccion</h3>
            </div>

            <Suspense fallback={
              <div className="flex min-h-[200px] items-center justify-center text-gray-400">
                <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-green-500" />
              </div>
            }>
              <AddressForm
                routes={routes}
                onSuccess={handleFormSuccess}
                hideMap={hideMap}
                initialNeighborhood={addresses.length === 0 ? discountNeighborhood : undefined}
              />
            </Suspense>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Selecciona donde quieres recibir tu pedido
      </p>

      <div className="mb-4 flex flex-col gap-2">
        {addresses.map((address) => {
          const isCompatible = isAddressCompatible ? isAddressCompatible(address) : true;
          const showDiscountWarning = isAddressCompatible && !isCompatible && address.is_deliverable;

          return (
            <button
              key={address.id}
              type="button"
              className={`flex w-full items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-200
                ${selectedAddressId === address.id ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'}
                ${!address.is_deliverable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              onClick={() => handleAddressSelect(address.id)}
              disabled={!address.is_deliverable}
            >
              <div className="flex-shrink-0 pt-0.5">
                {selectedAddressId === address.id ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#5cb85c">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="4" fill="white" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-medium leading-snug text-gray-800">{address.address}</div>
                {address.suite && <div className="mt-1 text-[13px] text-gray-500">{address.suite}</div>}
                {address.notes && <div className="mt-1 text-[13px] text-gray-500">{address.notes}</div>}
                {!address.is_deliverable && (
                  <div className="mt-1.5 text-xs font-medium text-red-600">Fuera de zona de entrega</div>
                )}
                {showDiscountWarning && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-600">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Descuento no aplica aquí
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {discountWarning && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0 text-amber-500">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm text-amber-800">{discountWarning}</p>
        </div>
      )}

      {hideAddButton ? (
        orgManageUrl && (
          <p className="mb-4 text-center text-sm text-gray-500">
            Administrá las direcciones de tu organización desde{' '}
            <a href={orgManageUrl} className="font-medium text-green-600 hover:text-green-700 underline">
              Mi Organización
            </a>
          </p>
        )
      ) : (
        <button
          type="button"
          className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-3.5 text-[15px] text-gray-500 transition-all duration-200 hover:border-green-500 hover:text-green-500"
          onClick={() => setShowForm(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Agregar nueva direccion
        </button>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="button"
        className="w-full rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        onClick={handleContinue}
        disabled={isLoading || selectedAddressId === null}
      >
        Continuar
      </button>
    </div>
  );
}
